import React from "react";
import { useNavigate } from "react-router-dom";
import { Receipt } from "lucide-react";
import {
  Card,
  colors,
  radius,
  spacing,
  typography,
} from "../../../design-system";
import { formatCurrency, getStubName } from "../helpers";
import { tdStyle, thStyle } from "../styles";
import type { PayStub } from "../types";

interface PayStubsTableProps {
  stubs: PayStub[];
  runId: string;
  currency?: string;
}

export default function PayStubsTable({
  stubs,
  runId,
  currency,
}: PayStubsTableProps) {
  const navigate = useNavigate();

  if (stubs.length === 0) {
    return (
      <Card noPadding>
        <div
          style={{
            padding: spacing[10] + "px " + spacing[8] + "px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              background: colors.bgCardActive,
              borderRadius: radius.pill,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: spacing[3],
            }}
          >
            <Receipt size={24} color={colors.textMuted} />
          </div>
          <div
            style={{
              ...typography.bodyStrong,
              color: colors.textPrimary,
              marginBottom: spacing[1],
            }}
          >
            No pay stubs yet
          </div>
          <p
            style={{
              ...typography.bodySm,
              color: colors.textSecondary,
              margin: 0,
            }}
          >
            Stubs will appear here once this run is calculated.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card noPadding>
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: typography.fontFamily,
            minWidth: 700,
          }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid " + colors.borderSubtle }}>
              <th style={thStyle}>Employee</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Gross</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Deductions</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Employer cost</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Net</th>
            </tr>
          </thead>
          <tbody>
            {stubs.map((stub, idx) => (
              <tr
                key={stub.id || idx}
                onClick={() =>
                  stub.id &&
                  navigate("/payroll/runs/" + runId + "/stubs/" + stub.id)
                }
                onMouseEnter={(e) => {
                  if (stub.id)
                    e.currentTarget.style.background = colors.bgCardHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = colors.bgCard;
                }}
                style={{
                  borderBottom:
                    idx < stubs.length - 1
                      ? "1px solid " + colors.borderSubtle
                      : "none",
                  cursor: stub.id ? "pointer" : "default",
                  transition: "background 150ms ease",
                }}
              >
                <td style={tdStyle}>
                  <div
                    style={{
                      ...typography.bodyMd,
                      color: colors.textPrimary,
                    }}
                  >
                    {getStubName(stub)}
                  </div>
                  {stub.employee_id && (
                    <div
                      style={{
                        ...typography.caption,
                        color: colors.textMuted,
                      }}
                    >
                      ID {String(stub.employee_id).slice(0, 8)}
                    </div>
                  )}
                </td>
                <td
                  style={{
                    ...tdStyle,
                    textAlign: "right",
                    fontFeatureSettings: '"tnum" 1',
                  }}
                >
                  {formatCurrency(
                    stub.gross_pay || stub.total_gross || stub.gross,
                    currency
                  )}
                </td>
                <td
                  style={{
                    ...tdStyle,
                    textAlign: "right",
                    color: colors.textSecondary,
                    fontFeatureSettings: '"tnum" 1',
                  }}
                >
                  {formatCurrency(
                    stub.total_deductions || stub.deductions,
                    currency
                  )}
                </td>
                <td
                  style={{
                    ...tdStyle,
                    textAlign: "right",
                    color: colors.textSecondary,
                    fontFeatureSettings: '"tnum" 1',
                  }}
                >
                  {formatCurrency(stub.employer_contributions, currency)}
                </td>
                <td
                  style={{
                    ...tdStyle,
                    textAlign: "right",
                    ...typography.bodyStrong,
                    color: colors.textPrimary,
                    fontFeatureSettings: '"tnum" 1',
                  }}
                >
                  {formatCurrency(
                    stub.net_pay || stub.total_net || stub.net,
                    currency
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}